import {
  DataRoomFile,
  SearchDealEntityType,
  SearchDealQuery,
  SearchDealResultType,
  SearchDealType,
  useActiveDealAccountRoleQuery,
  useDataRoomFolderQuery,
  useSearchDealQuery,
} from "@/src/graphql/generated";
import useGqlClient from "@/src/hooks/useGqlClient";
import {
  FilePlusIcon,
  FileSearch,
  FolderPlusIcon,
  LockIcon,
  MessageCircleQuestionIcon,
  SearchIcon,
  SearchXIcon,
  FileIcon as FileIconLucide,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import Loading from "../../Loading";
import { classNames } from "@/src/utils/cn";
import { ComboboxOption, ComboboxOptions } from "@headlessui/react";
import { FileIcon } from "../../FileIcon";
import { SearchResult } from "./search_result";
import clsx from "clsx";
import { usePermissionCheck } from "@/src/hooks/usePermissionCheck";

interface SearchResultsProps {
  searchTerm: string;
  recentSearches: DataRoomFile[];
  onClose: () => void;
  searchDealType?: SearchDealType;
  accountId?: string;
}

let searchTimeout: NodeJS.Timeout | null = null;

type FilterPill = {
  label: string;
  icon: any;
  filter: (
    results: SearchDealQuery["searchDeal"],
  ) => SearchDealQuery["searchDeal"];
};

const filterPills: FilterPill[] = [
  {
    label: "All",
    icon: SearchIcon,
    filter: (results) => results,
  },
  {
    label: "Data Room",
    icon: FileIconLucide,
    filter: (results) => {
      return results.filter((result) => {
        if (result.chunk) {
          return true;
        }

        if (!result.entity) {
          return false;
        }

        return (
          result.entity.entityType === SearchDealEntityType.DataRoomFile ||
          result.entity.entityType === SearchDealEntityType.DataRoomFolder
        );
      });
    },
  },
  {
    label: "Data Room (names only)",
    icon: FileSearch,
    filter: (results) => {
      return results.filter((result) => {
        if (result.chunk || !result.entity) {
          return false;
        }

        return (
          result.entity.entityType === SearchDealEntityType.DataRoomFile ||
          result.entity.entityType === SearchDealEntityType.DataRoomFolder
        );
      });
    },
  },
  {
    label: "Questions",
    icon: MessageCircleQuestionIcon,
    filter: (results) => {
      return results.filter((result) => {
        if (result.chunk || !result.entity) {
          return false;
        }

        return [
          SearchDealEntityType.Question,
          SearchDealEntityType.QuestionAnswer,
          SearchDealEntityType.QuestionAnswerFile,
          SearchDealEntityType.Ddql,
        ].includes(result.entity.entityType);
      });
    },
  },
  {
    label: "Access",
    icon: LockIcon,
    filter: (results) => {
      return results.filter((result) => {
        if (result.chunk || !result.entity) {
          return false;
        }

        return [
          SearchDealEntityType.DealAccount,
          SearchDealEntityType.DealGroup,
        ].includes(result.entity.entityType);
      });
    },
  },
];

const quickActions = [
  { name: "Add new file...", icon: FilePlusIcon, shortcut: "f", url: "#" },
  { name: "Add new folder...", icon: FolderPlusIcon, shortcut: "d", url: "#" },
];

export function SearchResults(props: SearchResultsProps) {
  const history = useHistory();
  const [results, setResults] = useState<SearchDealQuery["searchDeal"]>([]);
  const { dealId } = useParams<{ dealId: string }>();
  const [searchTerm, setSearchTerm] = useState("");
  const client = useGqlClient();

  const activeDealAccount = useActiveDealAccountRoleQuery(client, {
    id: dealId,
  });

  const activeDealAccountRole =
    activeDealAccount.data?.deal.activeDealAccount?.role;

  const searchQuery = useSearchDealQuery(
    client,
    {
      input: {
        query: searchTerm,
        dealID: dealId,
        type: props.searchDealType,
        accountID: props.accountId,
      },
    },
    {
      enabled: searchTerm.length > 2,
    },
  );

  const hasAccessPermission = usePermissionCheck({
    permissionKey: "access:has_access",
  });
  const [selectedFilter, setSelectedFilter] = useState(0);

  useEffect(() => {
    const term = props.searchTerm;

    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    searchTimeout = setTimeout(() => {
      setSearchTerm(term);
    }, 1000);
  }, [props.searchTerm]);

  useEffect(() => {
    if (searchTerm.length < 2) {
      setResults([]);
      return;
    }

    if (!searchQuery.data) {
      return;
    }

    const filter = filterPills[selectedFilter].filter;
    const res = filter(searchQuery.data.searchDeal);

    setResults(res);
  }, [searchTerm, searchQuery.data, selectedFilter]);

  if (searchQuery.isError && !searchQuery.data) {
    return (
      <div className="flex py-8 min-h-32 justify-center items-center">
        <p className="text-gray-600 text-sm">
          Something went wrong, please try again
        </p>
      </div>
    );
  }

  if (searchQuery.isLoading) {
    return (
      <div className="flex py-8 min-h-32 justify-center items-center">
        <Loading />
      </div>
    );
  }

  if (searchTerm === "") {
    return (
      <ComboboxOptions
        static
        className="max-h-80 scroll-py-2 overflow-y-auto pb-4"
      >
        <h2 className="mb-2 mt-4 px-6 text-xs font-semibold text-gray-500">
          Recently visited
        </h2>
        {props.recentSearches.length === 0 ? (
          <p className="text-sm px-6 text-gray-400">Nothing here yet</p>
        ) : (
          <div className="px-2">
            {props.recentSearches.map((file, i) => {
              return (
                <ComboboxOption
                  key={i}
                  value={file}
                  className={({ active }) =>
                    classNames(
                      "flex cursor-default select-none items-center rounded-md px-3 py-2",
                      active ? "bg-gray-700 text-white" : "",
                    )
                  }
                >
                  {({ active }) => (
                    <>
                      <div className="bg-white rounded-full h-7 w-7 justify-center items-center flex p-1">
                        <FileIcon fileType={file.fileType} />
                      </div>
                      <span className="ml-3 flex-auto truncate text-sm">
                        {file.parentFolders
                          .map((pf, i) => {
                            return `${
                              pf.name === "" ? "Home" : pf.name
                            }${" / "}`;
                          })
                          .reverse()}
                        <span className="font-bold">{file.name}</span>
                      </span>
                      {active && (
                        <span
                          className="ml-3 flex-none text-gray-100 text-sm cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            props.onClose();
                            setTimeout(() => {
                              history.push(
                                `/deal/${dealId}/documents/folder/${file.folderId}/file/${file.id}`,
                              );
                            }, 10);
                          }}
                        >
                          Jump to...
                        </span>
                      )}
                    </>
                  )}
                </ComboboxOption>
              );
            })}
          </div>
        )}
      </ComboboxOptions>
    );
  }

  if (!searchQuery.data || searchQuery.data.searchDeal.length === 0) {
    return (
      <div className="flex py-8 h-full justify-center items-center flex-col">
        <SearchXIcon className="w-6 h-6 text-gray-400" />
        <p className="text-gray-600 text-sm">
          No results found for that search term
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center gap-x-2 px-4 mt-3">
        {(hasAccessPermission ? filterPills : filterPills.slice(0, -1)).map(
          (fp, i) => {
            const selected = i === selectedFilter;
            return (
              <button
                onClick={() => {
                  setSelectedFilter(i);
                }}
                className={clsx(
                  "cursor-pointer shadow-sm flex items-center gap-x-1 px-1.5 py-1 border rounded-full border-concrete-200 hover:bg-concrete-100",
                  selected ? "border-concrete-500 bg-concrete-50" : "",
                )}
                type="button"
              >
                <fp.icon className="w-4 h-4 text-gray-500" />
                <p className="text-xs font-semibold text-gray-700">
                  {fp.label} ({fp.filter(searchQuery.data.searchDeal).length})
                </p>
              </button>
            );
          },
        )}
      </div>
      {results.length === 0 ? (
        <div className="px-4 mt-3">
          <p className="text-sm text-gray-500">
            No results for{" "}
            <span className="font-semibold">
              {filterPills[selectedFilter].label}
            </span>
          </p>
        </div>
      ) : null}
      <ComboboxOptions
        static
        className="max-h-[calc(100vh-20rem)] scroll-py-2 divide-y divide-gray-100 overflow-y-auto"
      >
        <li className="p-2 list-none">
          <ul className="text-sm text-gray-700">
            {results.map((result) => (
              <ComboboxOption
                key={resultKey(result)}
                value={result}
                className={({ active }) =>
                  classNames(
                    "flex cursor-default select-none items-center rounded-md px-2 py-1",
                    active ? "bg-gray-100 " : "",
                  )
                }
              >
                {({ focus }) => (
                  <>
                    <SearchResult
                      result={result}
                      key={resultKey(result)}
                      searchTerm={searchTerm}
                      isFocused={focus}
                    />
                  </>
                )}
              </ComboboxOption>
            ))}
          </ul>
        </li>
      </ComboboxOptions>
    </>
  );
}

function resultKey(result: SearchDealQuery["searchDeal"][0]) {
  if (result.resultType === SearchDealResultType.Entity) {
    return `${result.entity?.entityType}-${result.entity?.id}`;
  }

  if (result.resultType === SearchDealResultType.Chunk) {
    return `${result.chunk?.dataRoomFile?.id}-${result.chunk?.id}`;
  }

  return "";
}

function ReadAccessOnly(props: {
  children: React.ReactNode;
  folderId: string;
}) {
  const client = useGqlClient();
  const dataRoomFolderQuery = useDataRoomFolderQuery(
    client,
    {
      id: props.folderId,
    },
    {
      enabled: props.folderId !== "",
    },
  );

  if (
    dataRoomFolderQuery.isPending ||
    !dataRoomFolderQuery.data ||
    dataRoomFolderQuery.error
  ) {
    return null;
  }

  if (!dataRoomFolderQuery.data.dataRoomFolder.hasFolderWriteAccess) {
    return <>{props.children}</>;
  }

  return null;
}

function WriteAccessOnly(props: {
  children: React.ReactNode;
  folderId: string;
}) {
  const client = useGqlClient();
  const dataRoomFolderQuery = useDataRoomFolderQuery(
    client,
    {
      id: props.folderId,
    },
    {
      enabled: props.folderId !== "",
    },
  );

  if (
    dataRoomFolderQuery.isPending ||
    !dataRoomFolderQuery.data ||
    dataRoomFolderQuery.error
  ) {
    return null;
  }

  if (dataRoomFolderQuery.data.dataRoomFolder.hasFolderWriteAccess) {
    return <>{props.children}</>;
  }

  return null;
}
